import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDown, MoreHorizontal, Plus, Search } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Drawer } from '../../components/Drawer';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { cn } from '../../lib/cn';
import { usePackageCategories } from '../../api/catalog';
import { addAdminPackage, useAdminAllPackages, useAdminAllPrices } from '../../api/admin';
import { useQueryClient } from '@tanstack/react-query';
import { formatInt } from '../../lib/format';

const STATUS_TONE = {
  published: 'success' as const,
  draft: 'info' as const,
  deprecated: 'warning' as const,
  archived: 'neutral' as const,
};

const STATUS_LABEL = {
  published: 'Published',
  draft: 'Draft',
  deprecated: 'Deprecated',
  archived: 'Archived',
};

const VISIBILITY_LABEL = {
  all: 'All',
  specific_resellers: 'Specific',
  direct_only: 'Direct',
};

/** Packages list — table with status pills and bulk actions. */
export function Packages() {
  const { data: packages = [], isLoading } = useAdminAllPackages();
  const { data: pricesByPackage = [] } = useAdminAllPrices();
  const { data: packageCategories = [] } = usePackageCategories();
  // Default to the live platform catalog (matches /pricing): published + platform-owned.
  // Archived/draft and reseller-owned packages are one filter-click away.
  const [statusFilter, setStatusFilter] = useState('published');
  const [ownerFilter, setOwnerFilter] = useState('platform');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [newPkgOpen, setNewPkgOpen] = useState(false);

  const filtered = useMemo(() => {
    return packages.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (ownerFilter === 'platform' && p.soldBy !== 'all') return false;
      if (ownerFilter === 'reseller' && p.soldBy === 'all') return false;
      if (categoryFilter !== 'all' && String(p.categoryId ?? '') !== String(categoryFilter)) return false;
      if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.internalCode.includes(search.toLowerCase())) return false;
      return true;
    });
  }, [packages, statusFilter, ownerFilter, categoryFilter, search]);

  const categoryById = useMemo(
    () => Object.fromEntries(packageCategories.map((c) => [c.id, c])),
    [packageCategories],
  );

  const categoryFilterLabel =
    categoryFilter === 'all'
      ? 'All'
      : (categoryById[categoryFilter]?.displayName ?? 'All');

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  function toggleAll() {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((p) => p.id)));
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Pricing"
        title="Packages"
        subtitle="Stripe-style products. Each package can have many prices."
        action={
          <Button iconLeft={<Plus className="w-4 h-4" />} onClick={() => setNewPkgOpen(true)}>
            New package
          </Button>
        }
      />

      <NewPackageDrawer open={newPkgOpen} onClose={() => setNewPkgOpen(false)} />

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <FilterChip
            label="Status"
            value={statusFilter === 'all' ? 'All' : STATUS_LABEL[statusFilter as keyof typeof STATUS_LABEL]}
            options={[
              { v: 'all', l: 'All' },
              { v: 'published', l: 'Published' },
              { v: 'draft', l: 'Draft' },
              { v: 'deprecated', l: 'Deprecated' },
              { v: 'archived', l: 'Archived' },
            ]}
            onChange={setStatusFilter}
          />
          <FilterChip
            label="Category"
            value={categoryFilterLabel}
            options={[
              { v: 'all', l: 'All' },
              ...packageCategories.map((c) => ({ v: c.id, l: c.displayName })),
            ]}
            onChange={setCategoryFilter}
          />
          <FilterChip
            label="Reseller"
            value={ownerFilter === 'all' ? 'All' : ownerFilter === 'platform' ? 'Platform-owned' : 'Reseller-owned'}
            options={[
              { v: 'all', l: 'All' },
              { v: 'platform', l: 'Platform-owned' },
              { v: 'reseller', l: 'Reseller-owned' },
            ]}
            onChange={setOwnerFilter}
          />
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search packages…"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="px-5 sm:px-6 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleAll}
                    className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                </th>
                <th className="text-left font-medium px-4 py-3 sticky left-0 bg-zinc-50/50 dark:bg-zinc-900/50">Package</th>
                <th className="text-left font-medium px-4 py-3">Category</th>
                <th className="text-right font-medium px-4 py-3">Monthly</th>
                <th className="text-right font-medium px-4 py-3">Yearly</th>
                <th className="text-right font-medium px-4 py-3">Locations</th>
                <th className="text-right font-medium px-4 py-3">Users</th>
                <th className="text-right font-medium px-4 py-3 hidden md:table-cell">Products</th>
                <th className="text-right font-medium px-4 py-3 hidden md:table-cell">Invoices</th>
                <th className="text-right font-medium px-4 py-3 hidden lg:table-cell">Subscribers</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-3"><Skeleton className="w-4 h-4 rounded" /></td>
                    <td className="px-4 py-3 sticky left-0 bg-white dark:bg-zinc-900">
                      <Skeleton className="h-4 w-32 max-w-full rounded mb-1.5" />
                      <Skeleton className="h-3 w-40 max-w-full rounded" />
                    </td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-20 rounded-full" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded ml-auto" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded ml-auto" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-8 rounded ml-auto" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-8 rounded ml-auto" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><Skeleton className="h-4 w-10 rounded ml-auto" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><Skeleton className="h-4 w-10 rounded ml-auto" /></td>
                    <td className="px-4 py-3 hidden lg:table-cell"><Skeleton className="h-4 w-8 rounded ml-auto" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-5 sm:px-6 py-3"></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    {packages.length === 0 ? 'No packages yet. Create one to start selling.' : 'No packages match these filters.'}
                  </td>
                </tr>
              ) : (
                filtered.map((p) => {
                const prices = pricesByPackage.filter((pr) => pr.packageId === p.id);
                const monthly = prices.find((pr) => pr.interval === 'month' && pr.currency === 'BDT');
                const yearly = prices.find((pr) => pr.interval === 'year' && pr.currency === 'BDT');
                const fmt = (v: number | 'unlimited') => (v === 'unlimited' ? '∞' : formatInt(v));
                return (
                  <tr key={p.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                    <td className="px-5 sm:px-6 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(p.id)}
                        onChange={() => toggle(p.id)}
                        className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                      />
                    </td>
                    <td className="px-4 py-3 sticky left-0 bg-white dark:bg-zinc-900">
                      <Link to="/admin/packages/$id/edit" params={{ id: p.id }} className="block group">
                        <div className="font-medium text-zinc-950 dark:text-zinc-50 flex items-center gap-2 group-hover:text-brand dark:group-hover:text-brand-dm transition whitespace-nowrap">
                          {p.name}
                          {p.marketingBadge && <span className="text-[10px] uppercase tracking-wide bg-brand text-white dark:bg-brand-dm dark:text-zinc-950 px-1.5 py-px rounded">{p.marketingBadge}</span>}
                        </div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-400 max-w-xs truncate">{p.tagline}</div>
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone="neutral">
                        {categoryById[String(p.categoryId ?? '')]?.displayName ?? '—'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {monthly ? (
                        <Money
                          minor={monthly.amount}
                          currencyCode="BDT"
                          hideCents
                          tone="neutral"
                          className="text-zinc-950 dark:text-zinc-50 font-medium"
                        />
                      ) : (
                        <span className="text-zinc-400 dark:text-zinc-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {yearly ? (
                        <Money
                          minor={yearly.amount}
                          currencyCode="BDT"
                          hideCents
                          tone="neutral"
                          className="text-zinc-950 dark:text-zinc-50 font-medium"
                        />
                      ) : (
                        <span className="text-zinc-400 dark:text-zinc-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200">{fmt(p.limits.locations)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200">{fmt(p.limits.users)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200 hidden md:table-cell">{fmt(p.limits.products)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200 hidden md:table-cell">
                      {fmt(p.limits.invoices)}<span className="text-zinc-400 dark:text-zinc-500 text-[11px]">/mo</span>
                      {p.limits.invoicesYearly !== undefined && p.limits.invoicesYearly !== p.limits.invoices && (
                        <span className="block text-[11px] text-zinc-400 dark:text-zinc-500">{fmt(p.limits.invoicesYearly)}/yr</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-200 hidden lg:table-cell">
                      <Link to="/admin/packages/$id/subscribers" params={{ id: p.id }} className="hover:text-brand dark:hover:text-brand-dm">
                        {p.subscriberCount}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={STATUS_TONE[p.status]}>{STATUS_LABEL[p.status]}</Badge>
                    </td>
                    <td className="px-5 sm:px-6 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Link
                          to="/admin/packages/$id/edit"
                          params={{ id: p.id }}
                          className="px-2.5 py-1 rounded text-xs font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                        >
                          Edit
                        </Link>
                        <button className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition">
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
                })
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="text-xs text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50 border-t border-zinc-200 dark:border-zinc-700">
                <tr>
                  <td colSpan={12} className="px-5 sm:px-6 py-3">
                    <span className="font-medium text-zinc-700 dark:text-zinc-200">All packages include</span>
                    <span className="text-zinc-500 dark:text-zinc-400">: CRM · Essentials · Manufacturing · RMA · Service · Accounts · E-commerce</span>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="px-5 sm:px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 flex flex-wrap items-center gap-2">
          <span className="text-xs text-zinc-500 dark:text-zinc-400 mr-auto">
            Bulk actions:
          </span>
          <Button variant="secondary" size="sm" disabled={selected.size === 0}>Publish</Button>
          <Button variant="secondary" size="sm" disabled={selected.size === 0}>Deprecate</Button>
          <Button variant="secondary" size="sm" disabled={selected.size === 0}>Archive</Button>
        </div>
      </Card>
    </div>
  );
}

/* ── New package drawer ───────────────────────────────────────────────── */

function NewPackageDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: packageCategories = [] } = usePackageCategories();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [tagline, setTagline] = useState('');
  const [categoryId, setCategoryId] = useState('cat_pos');
  const [monthly, setMonthly] = useState('');
  const [yearly, setYearly] = useState('');
  const [locations, setLocations] = useState('1');
  const [users, setUsers] = useState('3');
  const [products, setProducts] = useState('1000');
  const [invoices, setInvoices] = useState('1000');
  const [unlimitedProducts, setUnlimitedProducts] = useState(false);
  const [unlimitedInvoices, setUnlimitedInvoices] = useState(false);
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [autoYearly, setAutoYearly] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const activeCategories = useMemo(
    () => packageCategories.filter((c) => c.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
    [packageCategories],
  );

  const computedYearly = autoYearly ? String(Number(monthly || '0') * 12) : yearly;
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const computedCode = code || slug;
  const valid = name.trim().length > 0 && Number(monthly) > 0;

  function reset() {
    setName('');
    setCode('');
    setTagline('');
    setCategoryId('cat_pos');
    setMonthly('');
    setYearly('');
    setLocations('1');
    setUsers('3');
    setProducts('1000');
    setInvoices('1000');
    setUnlimitedProducts(false);
    setUnlimitedInvoices(false);
    setStatus('draft');
    setAutoYearly(true);
    setSavedId(null);
  }

  function close() {
    onClose();
    setTimeout(reset, 250);
  }

  async function save() {
    if (!valid) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await addAdminPackage({
        name: name.trim(),
        internalCode: computedCode,
        tagline: tagline.trim() || undefined,
        categoryId,
        monthlyMinor: Math.round(Number(monthly) * 100),
        yearlyMinor: Math.round(Number(autoYearly ? computedYearly : yearly) * 100),
        status,
        features: {
          locations: Number(locations) || 1,
          users: Number(users) || 1,
          products: unlimitedProducts ? 'unlimited' : Number(products) || 0,
          invoices: unlimitedInvoices ? 'unlimited' : Number(invoices) || 0,
        },
      });
      await qc.invalidateQueries({ queryKey: ['admin', 'allPackages'] });
      await qc.invalidateQueries({ queryKey: ['admin', 'allPrices'] });
      await qc.invalidateQueries({ queryKey: ['packages'] }); // public catalog (pricing page, plan pickers)
      setSavedId(res?.id ?? 'pkg_' + computedCode);
    } catch (e) {
      const code = (e as { status?: number })?.status;
      setError(code === 409 ? 'A package with that code already exists.' : 'Could not create the package.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={close}
      title={savedId ? 'Package created' : 'New package'}
      width="lg"
      footer={
        savedId ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button onClick={save} loading={submitting} disabled={!valid}>
              {status === 'published' ? 'Publish package' : 'Save draft'}
            </Button>
          </>
        )
      }
    >
      {savedId ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950/40 grid place-items-center text-emerald-700 dark:text-emerald-300 mb-4">
            <Plus className="w-6 h-6" />
          </div>
          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
            {name} created
          </h3>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            Saved as <span className="mono">{computedCode}</span> in {status === 'published' ? 'published' : 'draft'} state.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <Input
            label="Name"
            required
            placeholder="Pro · Standard · Eid Special"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            label="Internal code"
            placeholder={slug || 'auto-generated-from-name'}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            hint="Immutable identifier used by webhooks. Auto-generated from name if blank."
          />
          <Input
            label="Tagline"
            placeholder="Most popular · For growing businesses"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
          />

          {/* ── Category picker ──────────────────────────────────────────
            * Drives whether signups under this package trigger Laravel POS
            * provisioning (when the chosen category has provisionsPos=true)
            * or are billing-only (school, internet, …).
            */}
          <div>
            <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
              Category
              <span className="text-rose-600 ml-0.5">*</span>
            </label>
            <div className="relative">
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className={cn(
                  'block w-full appearance-none px-3 py-2.5 pr-9 text-sm transition rounded-md',
                  'bg-white text-zinc-950',
                  'border border-zinc-300',
                  'focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand',
                  'dark:bg-zinc-900 dark:text-zinc-50 dark:border-zinc-700',
                  'dark:focus:ring-brand-dm dark:focus:border-brand-dm',
                )}
              >
                {activeCategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                    {c.provisionsPos ? ' · provisions POS' : ' · billing-only'}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            </div>
            <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">
              Categories where &quot;Provisions POS&quot; is on trigger Laravel POS database
              provisioning at signup.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Monthly price (BDT)"
              required
              type="number"
              min={0}
              placeholder="500"
              value={monthly}
              onChange={(e) => setMonthly(e.target.value)}
              prefix={<span className="text-sm">৳</span>}
            />
            <Input
              label="Yearly price (BDT)"
              type="number"
              min={0}
              placeholder={autoYearly ? `auto · ${computedYearly}` : '6000'}
              value={autoYearly ? computedYearly : yearly}
              onChange={(e) => setYearly(e.target.value)}
              disabled={autoYearly}
              prefix={<span className="text-sm">৳</span>}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
            <input
              type="checkbox"
              checked={autoYearly}
              onChange={(e) => setAutoYearly(e.target.checked)}
              className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
            />
            Auto-set yearly price = monthly × 12 (no annual discount, per spec)
          </label>

          <fieldset className="border-t border-zinc-200 dark:border-zinc-700 pt-5">
            <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-3">
              Resource limits
            </legend>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Locations" type="number" min={0} value={locations} onChange={(e) => setLocations(e.target.value)} />
              <Input label="Users" type="number" min={0} value={users} onChange={(e) => setUsers(e.target.value)} />
              <div>
                <Input
                  label="Products"
                  type="number"
                  min={0}
                  value={products}
                  onChange={(e) => setProducts(e.target.value)}
                  disabled={unlimitedProducts}
                />
                <label className="mt-1.5 flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={unlimitedProducts}
                    onChange={(e) => setUnlimitedProducts(e.target.checked)}
                    className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                  Unlimited products
                </label>
              </div>
              <div>
                <Input
                  label="Invoices / month"
                  type="number"
                  min={0}
                  value={invoices}
                  onChange={(e) => setInvoices(e.target.value)}
                  disabled={unlimitedInvoices}
                />
                <label className="mt-1.5 flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={unlimitedInvoices}
                    onChange={(e) => setUnlimitedInvoices(e.target.checked)}
                    className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
                  />
                  Unlimited invoices
                </label>
              </div>
            </div>
          </fieldset>

          <fieldset className="border-t border-zinc-200 dark:border-zinc-700 pt-5">
            <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
              Initial status
            </legend>
            <div className="space-y-2">
              <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="draft"
                  checked={status === 'draft'}
                  onChange={() => setStatus('draft')}
                  className="text-brand focus:ring-brand"
                />
                <span><strong>Draft</strong> — invisible to customers; finish setup before publishing</span>
              </label>
              <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="published"
                  checked={status === 'published'}
                  onChange={() => setStatus('published')}
                  className="text-brand focus:ring-brand"
                />
                <span><strong>Published</strong> — live on the pricing page immediately</span>
              </label>
            </div>
          </fieldset>

          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            All packages auto-include the standard module bundle (CRM · Essentials · Manufacturing · RMA · Service · Accounts · E-commerce). Edit the modules tab in the package editor to customize.
          </p>
        </div>
      )}
    </Drawer>
  );
}

function FilterChip({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ v: string; l: string }>;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
      >
        <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
        <span>{value}</span>
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 left-0 z-20 min-w-[180px] rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 shadow-md py-1">
            {options.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => {
                  onChange(o.v);
                  setOpen(false);
                }}
                className={cn(
                  'block w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-50 dark:hover:bg-zinc-800',
                  value === o.l ? 'text-brand dark:text-brand-dm font-medium' : 'text-zinc-700 dark:text-zinc-200',
                )}
              >
                {o.l}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
